import { addMinutes } from 'date-fns';
import { getUnixTimestamp, mapArrayToObject } from '../utils';
import { SensorType, SensorTypeFromApi, } from '../types';
import zipObj from 'ramda/src/zipObj';
import * as R from 'ramda';
export const formatDateForTimeSeriesApi = (date) => {
    return date.toISOString().split('.')[0] + 'Z';
};
export const determineWhichEnergyApiFunctionToUse = (useNewTsdb, iamApiFunction, tsApiFunction) => {
    if (useNewTsdb) {
        return tsApiFunction;
    }
    return iamApiFunction;
};
export const zipTSApiData = (data) => {
    if (!data)
        return [];
    return data.values.map((dataPoint) => {
        // zipObj(['a', 'b', 'c'], [1, 2, 3]) => {a: 1, b: 2, c: 3}
        return zipObj(data.columns, dataPoint);
    });
};
// Spillover Point is a 1-min aggregate window of last min of the time series
// so, adding one minute to this windows yields the last timestamp
export const extractSpilloverPointFromApiResponse = (data) => {
    if (data.spilloverPoint) {
        const { time, powerActive, current } = data.spilloverPoint;
        return time
            ? {
                energy: NaN,
                timestamp: getUnixTimestamp(addMinutes(new Date(time), 1)),
                power: powerActive / 1000,
                records: NaN,
                current: current,
            }
            : undefined;
    }
    else {
        return undefined;
    }
};
export const normalizeTSEnergyByEquipmentId = (data) => normalizeTSEnergyByResourceId(data);
export const normalizeTSEnergyByResourceId = (data) => {
    const zippedData = data && data.series ? zipTSApiData(data.series) : [];
    const totalPower = zippedData.reduce((total, { powerActive }) => {
        return (powerActive || 0) + total;
    }, 0);
    return {
        powerAverage: totalPower / data.consumption.samples,
        energyTotal: data.consumption.energy,
        readings: zippedData.map((data) => ({
            //power returns in W, convert to kW
            power: data.powerActive !== null ? data.powerActive / 1000 : null,
            current: data.current,
            energy: data.energy,
            //TODO: unsure if records === samples, waiting on confirmation from Alex
            records: data.samples,
            //time returns in ISO 8601, convert to unix timestamp
            timestamp: getUnixTimestamp(data.time),
        })),
        spilloverPoint: extractSpilloverPointFromApiResponse(data),
    };
};
export const normalizeTSFetchRecentPowerByEquipment = (data) => {
    const zippedData = zipTSApiData(data)[0];
    return {
        power: zippedData && zippedData.powerActive !== null
            ? zippedData.powerActive / 1000
            : null,
        resourceId: zippedData.equipmentId,
    };
};
export const normalizeTSEnergyDashboardBySiteId = (data) => {
    const zippedSeriesData = data.series ? zipTSApiData(data.series) : [];
    const zippedEquipmentGroupsData = zipTSApiData(data.consumptionByEquipmentGroups).map((d) => ({
        id: d.equipmentGroupId,
        energyTotal: d.energy,
    }));
    const zippedEquipmentTypesData = zipTSApiData(data.consumptionByEquipmentTypes).map((d) => ({
        id: d.equipmentTypeId,
        energyTotal: d.energy,
    }));
    const totalPower = zippedSeriesData.reduce((total, { powerActive }) => {
        return (powerActive || 0) + total;
    }, 0);
    return {
        consumption: {
            energyTotal: data.consumption.energy,
            //we don't use powerAverage, but keeping it to be consistent with IAM return
            powerAverage: totalPower / data.consumption.samples,
            readings: zippedSeriesData.map((data) => ({
                //power returns in W, convert to kW
                power: data.powerActive !== null ? data.powerActive / 1000 : null,
                current: data.current,
                energy: data.energy,
                //TODO: unsure if records === samples, waiting on confirmation from Alex
                records: data.samples,
                //time returns in ISO 8601, convert to unix timestamp
                timestamp: getUnixTimestamp(data.time),
            })),
        },
        consumptionByGroups: mapArrayToObject(zippedEquipmentGroupsData),
        consumptionByTypes: mapArrayToObject(zippedEquipmentTypesData),
        spilloverPoint: extractSpilloverPointFromApiResponse(data),
    };
};
export const normalizeTSEquipmentPowerBySiteId = (data) => {
    const zippedData = zipTSApiData(data);
    const processedData = zippedData.map((d) => ({
        id: d.equipmentId,
        powerAverage: d.powerActive !== null ? d.powerActive / 1000 : null,
    }));
    return mapArrayToObject(processedData);
};
export const normalizeTSSiteEnergyConsumption = (data) => {
    return {
        energyTotal: data.energy,
    };
};
export const getTimeSeriesMinMax = (timeSeries, comparisonKey) => {
    let max = Number.MIN_SAFE_INTEGER;
    let min = Number.MAX_SAFE_INTEGER;
    let tsHasData = false;
    timeSeries.forEach((datapoint) => {
        const value = datapoint[comparisonKey];
        if (typeof value !== 'number')
            return;
        tsHasData = true;
        if (value < min)
            min = value;
        if (value > max)
            max = value;
    });
    if (!tsHasData)
        return { min: undefined, max: undefined };
    return { min, max };
};
//TS Operational Sensor Data
export const createOperationalSamplesQueryString = (params) => {
    return Object.keys(params)
        .map((key) => `${key}=${Array.isArray(params[key]) ? params[key].join(',') : params[key]}`)
        .join('&');
};
export const convertQueryDatesToTsApiFormat = (queryParams) => {
    const updatedQueryParams = Object.assign({}, queryParams);
    Object.keys(queryParams).forEach((param) => {
        const paramValue = queryParams[param];
        if (param === 'to' || param === 'from') {
            const dateFormat = new Date(paramValue);
            updatedQueryParams[param] = formatDateForTimeSeriesApi(dateFormat);
        }
    });
    return updatedQueryParams;
};
export const OperationalSampleFromApiResponse = (data) => ({
    type: getSensorTypeEnum(data.type),
    sensorId: data.attributes.sensorId,
    timestamp: data.attributes.timestamp,
    value: storeSampleApiResponsiveValue(data.attributes),
});
export const storeSampleApiResponsiveValue = (responseAttributes) => {
    if (typeof responseAttributes.temperature === 'number') {
        return Number(responseAttributes.temperature.toFixed(1));
    }
    if (typeof responseAttributes.humidity === 'number') {
        return Number(responseAttributes.humidity.toFixed(1));
    }
    if (responseAttributes.state) {
        return (responseAttributes.state || [])[0];
    }
};
export const getSensorTypeEnum = (sensorType) => {
    if (sensorType === SensorTypeFromApi.STATE) {
        return SensorType.BINARY;
    }
    if (sensorType === SensorTypeFromApi.HUMIDITY) {
        return SensorType.HUMIDITY;
    }
    if (sensorType === SensorTypeFromApi.TEMPERATURE) {
        return SensorType.TEMPERATURE;
    }
};
const zipTSApiOperationalAmbientData = (data) => {
    if (!data)
        return [];
    const idxHumidity = data.columns.indexOf(SensorTypeFromApi.HUMIDITY);
    const idxTemp = data.columns.indexOf(SensorTypeFromApi.TEMPERATURE);
    let humidityRes = data.values.map((dataPoint) => {
        return zipObj(['type', data.columns[0], data.columns[1]], [SensorTypeFromApi.HUMIDITY, dataPoint[0], dataPoint[idxHumidity]]);
    });
    let tempRes = data.values.map((dataPoint) => {
        return zipObj(['type', data.columns[0], data.columns[2]], [SensorTypeFromApi.TEMPERATURE, dataPoint[0], dataPoint[idxTemp]]);
    });
    const unsortedAmbientData = humidityRes.concat(tempRes);
    return R.sortWith([R.ascend(R.prop('time')), R.ascend(R.prop('type'))], unsortedAmbientData);
};
const zipTSApiOperationalDataWithType = (data) => {
    if (!data)
        return [];
    const type = data.columns[1];
    return data.values.map((dataPoint) => {
        data.columns.push('type');
        dataPoint.push(type);
        return zipObj(data.columns, dataPoint);
    });
};
const zipDataToIndividualType = (data) => {
    if (data.columns.includes(SensorTypeFromApi.TEMPERATURE) &&
        data.columns.includes(SensorTypeFromApi.HUMIDITY)) {
        return zipTSApiOperationalAmbientData(data).reverse();
    }
    return zipTSApiOperationalDataWithType(data).reverse();
};
export const normalizeTSOperationalDataBySensorId = (data, sensorId) => {
    return zipDataToIndividualType(data).map((d) => ({
        type: d.type,
        attributes: {
            sensorId: sensorId,
            timestamp: getUnixTimestamp(d.time),
            temperature: d.temperature !== null ? d.temperature : null,
            humidity: d.humidity !== null ? d.humidity : null,
            state: d.state !== null ? [d.state] : null,
        },
    }));
};
